/* eslint-disable max-lines */
import styled from 'styled-components';

export const BlockingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.25s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f1f5f9')};
  border-radius: 6px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#64748b')};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#4b5563' : '#e2e8f0')};
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f8fafc' : '#475569')};
  }

  &:active {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#6b7280' : '#cbd5e1')};
  }
`;

export const NoOrgCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 32px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : 'white')};
  border-radius: 6px;
  box-shadow: ${({ theme }) => (theme.mode === 'dark'
    ? '0 25px 50px -12px rgba(255, 255, 255, 0.1)'
    : '0 25px 50px -12px rgba(224, 123, 45, 0.2)')};
  max-width: 420px;
  width: 100%;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const ModalCard = styled.div`
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : 'white')};
  border-radius: 14px;
  box-shadow: 0 20px 40px -12px rgba(15, 23, 42, 0.25);
  max-width: 380px;
  width: 100%;
  overflow: hidden;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const ModalCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px 12px;
  border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f1f5f9')};
`;

export const ModalCardBody = styled.div`
  padding: 12px 18px;
`;

export const ModalCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f1f5f9')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : '#f8fafc')};
`;

export const OrgSelectorContainer = styled.div<{ $isCollapsed: boolean }>`
  width: 100%;
  padding: ${({ $isCollapsed }) => ($isCollapsed ? '6px' : '4px 8px')};
`;

export const TriggerButton = styled.button<{ $isCollapsed: boolean; $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: ${({ $isCollapsed }) => ($isCollapsed ? '6px' : '6px 8px')};
  background: ${({ $isOpen, theme }) => {
    if (!$isOpen) return 'transparent';
    return theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6';
  }};
  border: 1px solid ${({ $isOpen, theme }) => {
    if (!$isOpen) return 'transparent';
    return theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#e5e7eb';
  }};
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  text-align: left;

  &:hover:not(:disabled) {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6')};
    border-color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#e5e7eb')};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${({ $isCollapsed }) => $isCollapsed
      && `
    justify-content: center;
    padding: 8px;
  `}
`;

export const StaticOrgDisplay = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${({ $isCollapsed }) => ($isCollapsed ? '6px' : '6px 8px')};

  ${({ $isCollapsed }) => $isCollapsed
      && `
    justify-content: center;
    padding: 8px;
  `}
`;

export const LogoWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

export const StatusDot = styled.div`
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  background: #22c55e;
  border: 2px solid ${({ theme }) => (theme.mode === 'dark' ? '#1a1f26' : '#ffffff')};
  border-radius: 50%;
`;

export const PlanBadge = styled.div<{ $plan: string }>`
  position: absolute;
  top: -6px;
  right: -6px;
  padding: 2px 6px;
  font-size: 9px;
  font-weight: 400;
  border-radius: 4px;
  text-transform: uppercase;
  white-space: nowrap;
  z-index: 1;
  background: ${({ $plan }) => {
    switch ($plan) {
      case 'TEST': return '#f59e0b';
      case 'BASE': return '#3b82f6';
      case 'PRO': return '#8b5cf6';
      case 'ULTIMATE': return '#eab308';
      case 'CUSTOM': return '#ec4899';
      case 'INDIVIDUAL': return '#14b8a6';
      default: return '#6b7280';
    }
  }};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

export const OrgInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const OrgName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#111827')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MenuItem = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: ${({ $isActive, theme }) => {
    if ($isActive) return theme.mode === 'dark' ? 'rgba(224, 123, 45, 0.15)' : '#e6f2f2';
    return 'transparent';
  }};
  border: 1px solid ${({ $isActive, theme }) => {
    if ($isActive) return theme.mode === 'dark' ? '#1a4d4d' : '#96c8c8';
    return 'transparent';
  }};
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  text-align: left;

  &:hover:not(:disabled) {
    background: ${({ $isActive, theme }) => {
    if ($isActive) return theme.mode === 'dark' ? 'rgba(224, 123, 45, 0.2)' : '#e6f2f2';
    return theme.mode === 'dark' ? '#374151' : '#f8fafc';
  }};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#94a3b8')};
  font-size: 14px;
`;

export const PrimaryButton = styled.button`
    padding: 7px 14px;
    /* Klasyczny czarny przycisk — spójny z komponentem AddButton. */
    background: #111827;
    color: white;
    border: 1px solid transparent;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 300;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
    transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;

    &:hover:not(:disabled) {
        background: #000000;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28);
    }

    &:active:not(:disabled) {
        transform: scale(0.97);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }

    ${({ theme }) => (theme.mode === 'dark'
    ? `
      background: #1f2937;
      border-color: #374151;

      &:hover:not(:disabled) {
        background: #111827;
      }
    `
    : '')}
`;
